import { ExternalLink } from 'lucide-react'
import { toast } from 'sonner'

import { Badge, type BadgeVariant } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { ConfirmationDialog } from '@/components/ui/confirmation-dialog'
import { parseError } from '@/lib/errors'
import { formatDateTimeShort } from '@/lib/formatters'
import { endorsementSteps, useEndorsementStep } from '@/models/internalEndorsement'
import type { CorgiEndorsement, PolicyChangeQuote } from '@/models/policyChange'

const STATUS: Record<string, { label: string; variant: BadgeVariant }> = {
  draft: { label: 'Draft on Corgi', variant: 'secondary' },
  quoting: { label: 'Corgi is pricing it', variant: 'secondary' },
  quoted: { label: 'Priced by Corgi', variant: 'success' },
  pending_payment: { label: 'Corgi: pending payment', variant: 'secondary' },
  bound: { label: 'Bound on Corgi', variant: 'success' },
  active: { label: 'Active on Corgi', variant: 'success' },
  manual_review: { label: 'Corgi is reviewing it', variant: 'warning' },
  expired: { label: 'Corgi quote expired', variant: 'warning' },
  cancelled: { label: 'Cancelled on Corgi', variant: 'outline' },
}

function statusOf(endorsement: CorgiEndorsement) {
  return (
    STATUS[endorsement.status] ?? {
      label: `Corgi: ${endorsement.status || 'unknown'}`,
      variant: 'outline' as const,
    }
  )
}

interface CorgiQuoteProps {
  endorsementId: string
  quote: PolicyChangeQuote
  disabled?: boolean
}

const DEAD_STATUSES = new Set(['cancelled', 'expired'])

/**
 * The Corgi buttons for one policy's quote, for the request's row of steps: Send to Corgi while no draft is live
 * and the request is with the carriers (the drafting on send failed, or every draft was cancelled), else submit the
 * drafts to Corgi for pricing and read Corgi's status and prices back.
 */
export function CorgiQuoteButtons({
  endorsementId,
  quote,
  disabled,
  withCarrier = false,
}: CorgiQuoteProps & { withCarrier?: boolean }) {
  const send = useEndorsementStep(endorsementSteps.buildCorgiDraft)
  const submit = useEndorsementStep(endorsementSteps.submitCorgiQuote)
  const sync = useEndorsementStep(endorsementSteps.syncCorgiQuote)

  if (!quote.corgiEligible) return null
  const { endorsements } = quote.corgiDraft
  const live = endorsements.filter((endorsement) => !DEAD_STATUSES.has(endorsement.status))

  if (live.length === 0) {
    if (!withCarrier) return null
    return (
      <ConfirmationDialog
        title="Send this request to Corgi?"
        description="Cai opens a draft endorsement on the customer's Corgi auto policies: trucks and trailers taken off or added go on auto liability and physical damage, drivers on auto liability. Cargo and general liability are rated per fleet, so they are left alone. On the customer's behalf it confirms Corgi's three statements: no incidents that could give rise to a claim since the policy started, no claims filed or notice given, and no material change to the business. Nothing is submitted yet."
        confirmLabel="Send to Corgi"
        onConfirm={async () => {
          try {
            await send.mutateAsync({ id: endorsementId, quoteId: quote.id })
            toast.success('Drafted on Corgi. Check the drafts, then submit them to Corgi for a quote.')
          } catch (error) {
            toast.error(parseError(error))
          }
        }}
      >
        <Button
          type="button"
          variant="outline"
          disabled={disabled}
          loading={send.isPending ? 'Sending…' : undefined}
        >
          Send to Corgi
        </Button>
      </ConfirmationDialog>
    )
  }

  const hasDraft = withCarrier && endorsements.some((endorsement) => endorsement.status === 'draft')
  return (
    <>
      {hasDraft && (
        <ConfirmationDialog
          title="Submit the drafts to Corgi for a quote?"
          description="Corgi prices each draft as it stands now; anything still to add goes in the portal first. Cai reads the price back right away and the quote fills in here within moments."
          confirmLabel="Submit to Corgi"
          onConfirm={async () => {
            try {
              await submit.mutateAsync({ id: endorsementId, quoteId: quote.id })
              toast.success('Submitted to Corgi. The quote fills in here within moments.')
            } catch (error) {
              toast.error(parseError(error))
            }
          }}
        >
          <Button type="button" disabled={disabled} loading={submit.isPending ? 'Submitting…' : undefined}>
            Submit to Corgi for a quote
          </Button>
        </ConfirmationDialog>
      )}
      <Button
        type="button"
        variant="outline"
        disabled={disabled}
        loading={sync.isPending ? 'Refreshing…' : undefined}
        onClick={() =>
          sync.mutate(
            { id: endorsementId, quoteId: quote.id },
            {
              onSuccess: () => toast.success('Refreshed from Corgi.'),
              onError: (error) => toast.error(parseError(error)),
            }
          )
        }
      >
        Refresh from Corgi
      </Button>
    </>
  )
}

/** Where one policy's Corgi drafts stand: each with its status and portal link, when Cai last looked, and what is left for a person. */
export function CorgiQuoteStatus({ quote }: { quote: PolicyChangeQuote }) {
  if (!quote.corgiEligible) return null
  const { endorsements } = quote.corgiDraft
  const notes = quote.corgiDraft.notes ?? []
  if (endorsements.length === 0 && notes.length === 0) return null

  const stale = endorsements.some((endorsement) => endorsement.stale)
  const pricing = endorsements.some((endorsement) =>
    ['quoting', 'manual_review'].includes(endorsement.status)
  )
  const lastSynced =
    endorsements
      .map((e) => e.syncedAt)
      .filter(Boolean)
      .sort()
      .at(-1) ?? null

  return (
    <div className="mt-2 flex flex-col gap-1 text-sm">
      {endorsements.map((endorsement) => {
        const status = statusOf(endorsement)
        return (
          <div key={endorsement.endorsementId} className="flex flex-wrap items-center gap-2">
            <span className="font-medium">{endorsement.policyNumber}</span>
            <Badge variant={endorsement.stale ? 'warning' : status.variant}>
              {endorsement.stale ? 'Changed since' : status.label}
            </Badge>
            {endorsement.portalUrl && (
              <a
                className="inline-flex items-center gap-1 underline"
                href={endorsement.portalUrl}
                target="_blank"
                rel="noreferrer"
              >
                Open in Corgi portal <ExternalLink className="size-3.5" />
              </a>
            )}
            {lastSynced && (
              <span className="text-xs text-neutral-500">Checked {formatDateTimeShort(lastSynced)}</span>
            )}
          </div>
        )
      })}
      {pricing && !stale && (
        <p className="text-xs text-neutral-600">
          Cai reads the price back right after submitting and this page refreshes as it lands; Refresh from
          Corgi looks now.
        </p>
      )}
      {stale && (
        <p className="text-xs text-amber-700">
          A change on a Corgi draft was dropped from this request since, so Corgi’s price no longer fits.
          Cancel the draft in the portal and send the request again.
        </p>
      )}
      {notes.length > 0 && (
        <ul className="list-disc pl-5 text-xs text-neutral-600">
          {notes.map((line) => (
            <li key={line}>{line}</li>
          ))}
        </ul>
      )}
    </div>
  )
}
